import React from 'react';
import { Modal, Form, Input, Select as AntSelect } from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    SafetyCertificateOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import Select from '@/components/ui/Select';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import AppModal from '@/pages/inside/tenant/AppModal';
import EmployeeDetailPanel from '../components/EmployeeDetailPanel';
import AttendanceDetail from '../../attendance/screen/AttendanceDetail';
import { useEmployees } from '../hooks/useEmployees';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    RolesSliderTabWrap,
    RolesSearchWrapper,
    RolesSearchInput,
    RolesFilterBtn,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ErrorBlock,
} from '@/pages/inside/tenant/style';

const Employees = () => {
    const {
        total,
        activeCount,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        filterRole,
        setFilterRole,
        filterRoleOptions,
        activeFilterCount,
        clearFilters,
        panelOpen,
        closePanel,
        openPanel,
        selectedEmployeeId,
        selectedEmployeeName,
        canView,
        canViewAll,
        canCreate,
        createModalOpen,
        setCreateModalOpen,
        createForm,
        onSubmitCreate,
        closeCreateModal,
        createEmployeePending,
        roleOptions,
        statusOptions,
        editModalOpen,
        closeEditModal,
        editForm,
        employeeForEdit,
        onSubmitEdit,
        updateEmployeePending,
        removalConfirmOpen,
        closeRemovalConfirm,
        employeeForRemoval,
        confirmRequestRemoval,
        requestRemovalPending,
        removeUserModalOpen,
        employeeForRemoveUser,
        closeRemoveUserModal,
        confirmRemoveUser,
        openRemoveUserConfirm,
        removeUserPending,
        canRemoveUser,
        openEditModal,
        openRemovalConfirm,
        canUpdate,
        canRequestHrRemoval,
        hasPendingRemoval,
        employees,
        employeesError,
        schedulesList,
    } = useEmployees();

    const scheduleOptions = React.useMemo(() => {
        return (schedulesList || []).map((s) => ({
            value: s.id,
            label: `${s.title} (${s.startTime} - ${s.endTime})`,
        }));
    }, [schedulesList]);

    const selectedEmployee = employees.find((e) => e.id === selectedEmployeeId) ?? null;

    if (!canViewAll) {
        return <AttendanceDetail />;
    }

    return (
        <ListBodyLayout
            title="Employees"
            description="Manage HR employees"
            actions={
                canCreate && (
                    <Button
                        $primary
                        $blackOnLight
                        icon={<PlusOutlined />}
                        onClick={() => setCreateModalOpen(true)}
                        $padding="10px 20px"
                        $size="14px"
                    >
                        Create Employee
                    </Button>
                )
            }
            kpiCards={[
                {
                    label: 'Total Employees',
                    value: total,
                    icon: <TeamOutlined style={{ color: 'var(--ant-color-primary, #1890ff)' }} />,
                    trend: 'in workspace',
                    trendPositive: true,
                },
                {
                    label: 'Active',
                    value: activeCount,
                    icon: <CheckCircleOutlined style={{ color: '#10b981' }} />,
                    trend: 'active employees',
                    trendPositive: true,
                },
                {
                    label: 'With Roles',
                    value: tableData.filter((r) => r.roles?.length).length,
                    icon: <SafetyCertificateOutlined style={{ color: '#8b5cf6' }} />,
                    trend: 'role assigned',
                    trendPositive: true,
                },
                {
                    label: 'Employees',
                    value: total,
                    icon: <UserOutlined style={{ color: '#ef4444' }} />,
                    trend: 'HR managed',
                    trendPositive: true,
                },
            ]}
        >
            {employeesError && (
                <ErrorBlock>
                    Unable to load employees. Check that the HR API (GET /api/v1/hr/employees) is available.
                </ErrorBlock>
            )}

            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="16px"
            >
                <Container $display="flex" $alignItems="center" $minWidth="0">
                    <RolesSliderTabWrap>
                        <SliderTab
                            items={[
                                { key: STATUS_TAB.ALL, label: 'All' },
                                { key: STATUS_TAB.ACTIVE, label: 'Active' },
                                { key: STATUS_TAB.INACTIVE, label: 'Inactive' },
                            ]}
                            activeKey={statusTab}
                            onChange={setStatusTab}
                            fitContent
                        />
                    </RolesSliderTabWrap>
                </Container>
                <Container $display="flex" $alignItems="center" $gap="10px" $flexWrap="wrap">
                    <RolesSearchWrapper>
                        <SearchOutlined />
                        <RolesSearchInput
                            placeholder="Search here"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </RolesSearchWrapper>
                    <Select
                        placeholder="All roles"
                        value={filterRole}
                        onChange={setFilterRole}
                        options={filterRoleOptions}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                    />
                    {activeFilterCount > 0 && (
                        <RolesFilterBtn type="button" onClick={clearFilters}>
                            Clear filters
                        </RolesFilterBtn>
                    )}
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
                onRow={(record) => ({
                    onClick: () => canView && openPanel(record.id),
                    style: { cursor: canView ? 'pointer' : 'default' },
                })}
            />

            <AppModal
                size="md"
                title={selectedEmployeeName}
                open={panelOpen}
                onCancel={closePanel}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <EmployeeDetailPanel
                    employeeId={selectedEmployeeId}
                    employee={selectedEmployee}
                    canUpdate={canUpdate}
                    canRequestHrRemoval={canRequestHrRemoval}
                    canRemoveUser={canRemoveUser}
                    hasPendingRemoval={selectedEmployee ? hasPendingRemoval(selectedEmployee) : false}
                    onEdit={openEditModal}
                    onRequestRemoval={openRemovalConfirm}
                    onRemoveUser={openRemoveUserConfirm}
                />
            </AppModal>

            <AppModal
                size="md"
                title="Create Employee"
                open={createModalOpen}
                onCancel={closeCreateModal}
                footer={null}
                destroyOnHidden
                width={480}
                zIndex={1100}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={createForm} layout="vertical">
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: 'Enter email' },
                                { type: 'email', message: 'Enter a valid email' },
                            ]}
                        >
                            <Input placeholder="e.g. jane@company.com" />
                        </Form.Item>
                        <Form.Item name="name" label="Full name" rules={[{ required: true, message: 'Enter name' }]}>
                            <Input placeholder="e.g. Jane Doe" />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[
                                { required: true, message: 'Enter password' },
                                { min: 8, message: 'At least 8 characters' },
                            ]}
                        >
                            <Input.Password placeholder="Minimum 8 characters" />
                        </Form.Item>
                        <Form.Item name="roleId" label="Role" rules={[{ required: true, message: 'Select a role' }]}>
                            <AntSelect
                                placeholder="Select role"
                                options={roleOptions}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item name="scheduleGroupId" label="Schedule">
                            <AntSelect
                                placeholder="Select schedule (optional)"
                                options={scheduleOptions}
                                showSearch
                                optionFilterProp="label"
                                allowClear
                            />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} onClick={closeCreateModal} $padding="10px 16px" $size="14px">
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        onClick={onSubmitCreate}
                        loading={createEmployeePending}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Create
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <Modal
                maskClosable={false}
                title="Edit Employee"
                open={editModalOpen}
                onCancel={closeEditModal}
                footer={null}
                destroyOnHidden
                width={480}
                zIndex={1100}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={editForm} layout="vertical">
                        <Form.Item name="roleId" label="Role" rules={[{ required: true, message: 'Select a role' }]}>
                            <AntSelect
                                placeholder="Select role"
                                options={roleOptions}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Select status' }]}>
                            <AntSelect placeholder="Status" options={statusOptions} />
                        </Form.Item>
                        <Form.Item name="scheduleGroupId" label="Schedule">
                            <AntSelect
                                placeholder="Select schedule (optional)"
                                options={scheduleOptions}
                                showSearch
                                optionFilterProp="label"
                                allowClear
                            />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} onClick={closeEditModal} $padding="10px 16px" $size="14px">
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        onClick={onSubmitEdit}
                        loading={updateEmployeePending}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Save
                    </Button>
                </CreateRoleModalFooter>
            </Modal>

            <Modal
                maskClosable={false}
                title="Request removal"
                open={removalConfirmOpen}
                onCancel={closeRemovalConfirm}
                onOk={confirmRequestRemoval}
                okText="Request removal"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                confirmLoading={requestRemovalPending}
                zIndex={1100}
            >
                {employeeForRemoval && (
                    <p>
                        Request removal of <strong>{employeeForRemoval.name}</strong> ({employeeForRemoval.email})? A
                        Company Admin must approve before the employee is removed from the company.
                    </p>
                )}
            </Modal>

            <Modal
                maskClosable={false}
                title="Remove User"
                open={removeUserModalOpen}
                onCancel={closeRemoveUserModal}
                onOk={confirmRemoveUser}
                okText="Remove User"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                confirmLoading={removeUserPending}
                zIndex={1100}
            >
                {employeeForRemoveUser && (
                    <p>
                        Remove <strong>{employeeForRemoveUser.name}</strong> ({employeeForRemoveUser.email}) from the
                        company? They will be removed from this workspace.
                    </p>
                )}
            </Modal>
        </ListBodyLayout>
    );
};

export default Employees;
